<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2>公開データセット作成</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <form @submit.prevent="handleSubmit">
        <div class="form-group">
          <label for="name">データセット名 <span class="required">*</span></label>
          <input
            id="name"
            v-model="form.name"
            type="text"
            required
            placeholder="例: プレミアムPOI（東京）"
          />
        </div>

        <div class="form-group">
          <label for="description">説明</label>
          <textarea
            id="description"
            v-model="form.description"
            rows="3"
            placeholder="データセットの説明を入力してください"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="price">検索1回あたりの価格（円） <span class="required">*</span></label>
          <input
            id="price"
            v-model.number="form.price_per_search"
            type="number"
            step="0.01"
            min="0"
            max="1000"
            required
            placeholder="0.00"
          />
          <small>0.00 = 無料、最大1000円まで</small>
        </div>

        <!-- プラットフォーム管理者専用: ユーザー選択フィールド -->
        <template v-if="isPlatformAdmin">
          <div class="form-group">
            <label for="assigned_user">POI編集担当ユーザー</label>
            <select
              id="assigned_user"
              v-model="form.assigned_user_id"
              class="form-select"
            >
              <option value="">プラットフォーム管理（自分）</option>
              <option
                v-for="user in users"
                :key="user.id"
                :value="user.id"
              >
                {{ user.display_name }} ({{ user.email }}) - {{ getRoleLabel(user.account_type) }}
              </option>
            </select>
            <small>POIの追加・編集を行うユーザーを選択</small>
          </div>

          <div class="form-group">
            <label for="provider_user">収益受取ユーザー</label>
            <select
              id="provider_user"
              v-model="form.provider_user_id"
              class="form-select"
            >
              <option value="">プラットフォーム収益</option>
              <option
                v-for="user in users"
                :key="user.id"
                :value="user.id"
              >
                {{ user.display_name }} ({{ user.email }}) - {{ getRoleLabel(user.account_type) }}
              </option>
            </select>
            <small>データセット利用料の収益を受け取るユーザーを選択</small>
          </div>
        </template>

        <div v-if="loadingUsers" class="loading-message">
          <span>ユーザー一覧を読み込み中...</span>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-secondary">
            キャンセル
          </button>
          <button type="submit" :disabled="loading || loadingUsers" class="btn-primary">
            {{ loading ? '作成中...' : '作成' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '../../stores/auth.js'
import { useDatasetStore } from '../../stores/dataset.js'
import { userManagementApi } from '../../api/admin.js'

const emit = defineEmits(['close', 'created'])
const authStore = useAuthStore()
const datasetStore = useDatasetStore()

const form = ref({
  name: '',
  description: '',
  price_per_search: 0,
  assigned_user_id: '',
  provider_user_id: '',
})

const users = ref([])
const loading = ref(false)
const loadingUsers = ref(false)
const error = ref(null)

const isPlatformAdmin = computed(() => authStore.user?.account_type === 'platform_admin')

function getRoleLabel(accountType) {
  const labels = {
    'data_provider': 'データプロバイダー',
    'platform_admin': '管理者',
    'trial': 'トライアル',
    'individual': '個人',
    'corporate': '法人',
  }
  return labels[accountType] || accountType
}

async function loadUsers() {
  if (!isPlatformAdmin.value) return

  loadingUsers.value = true
  try {
    const response = await userManagementApi.listUsers({ account_type: 'data_provider', limit: 100 })
    users.value = response.data.users
  } catch (err) {
    console.error('Failed to load users:', err)
  } finally {
    loadingUsers.value = false
  }
}

async function handleSubmit() {
  loading.value = true
  error.value = null

  try {
    const payload = {
      name: form.value.name,
      description: form.value.description,
      price_per_search: form.value.price_per_search,
    }

    // プラットフォーム管理者の場合はassignDataset、それ以外はcreateDataset
    if (isPlatformAdmin.value) {
      if (form.value.assigned_user_id) {
        payload.assigned_user_id = form.value.assigned_user_id
      }
      if (form.value.provider_user_id) {
        payload.provider_user_id = form.value.provider_user_id
      }
      await datasetStore.assignDataset(payload)
    } else {
      await datasetStore.createDataset(payload)
    }

    emit('created')
  } catch (err) {
    error.value = err.response?.data?.error?.message || 'データセットの作成に失敗しました'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  console.log('[CreateDatasetModal] Current user:', authStore.user)
  console.log('[CreateDatasetModal] User account_type:', authStore.user?.account_type)
  console.log('[CreateDatasetModal] isPlatformAdmin:', isPlatformAdmin.value)
  loadUsers()
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 2rem;
  height: 2rem;
  line-height: 1;
}

.btn-close:hover {
  color: #212529;
}

form {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.required {
  color: #dc3545;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-select {
  background-color: white;
  cursor: pointer;
}

.form-group small {
  display: block;
  margin-top: 0.25rem;
  color: #6c757d;
  font-size: 0.875rem;
}

.loading-message {
  padding: 0.75rem;
  margin-bottom: 1rem;
  background-color: #d1ecf1;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  color: #0c5460;
  text-align: center;
}

.error-message {
  padding: 0.75rem;
  margin-bottom: 1rem;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #dee2e6;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-primary:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}
</style>
